import { useMemo, useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";

import { queryKeys } from "@nasam/kernel";
import { useDebouncedValue } from "../../../shared/application/useDebouncedValue";
import type {
  SensorAssignmentsListQuery,
  SensorAssignmentsRepository,
  SensorType,
} from "../domain";

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;

export interface SensorOption {
  readonly value: string;
  readonly label: string;
}

export interface UseSensorOptionsResult {
  readonly options: readonly SensorOption[];
  readonly searchValue: string;
  readonly onSearchChange: (value: string) => void;
  readonly isLoading: boolean;
  readonly isFetchingMore: boolean;
  readonly hasMore: boolean;
  readonly onLoadMore: () => void;
}

/**
 * A searchable, paged Sensor picker over `GET /sensors` -- each option is the
 * sensor's name with its device code, since names repeat across devices.
 * Lives beside the repository it reads so a sibling feature (e.g. Alert
 * History's Sensor filter) can reuse it through `contracts`. `accountId`
 * scopes the list for a staff caller; a client user is always limited to
 * their own company by the backend.
 */
export function useSensorOptions(
  repository: SensorAssignmentsRepository | undefined,
  accountId?: string,
  {
    sensorType,
    enabled = true,
  }: { readonly sensorType?: SensorType; readonly enabled?: boolean } = {},
): UseSensorOptionsResult {
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebouncedValue(searchTerm, SEARCH_DEBOUNCE_MS);
  const search =
    debouncedSearch.trim() === "" ? undefined : debouncedSearch.trim();

  const listQuery = (page: number): SensorAssignmentsListQuery => ({
    page,
    limit: PAGE_SIZE,
    ...(search === undefined ? {} : { search }),
    ...(accountId === undefined ? {} : { accountId }),
    ...(sensorType === undefined ? {} : { sensorType }),
  });

  const query = useInfiniteQuery({
    queryKey: queryKeys.sensors.list({
      options: true,
      limit: PAGE_SIZE,
      search,
      accountId,
      sensorType,
    }),
    queryFn: ({ pageParam }) => repository!.list(listQuery(pageParam)),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.meta.currentPage < lastPage.meta.totalPages
        ? lastPage.meta.currentPage + 1
        : undefined,
    enabled: enabled && repository !== undefined,
  });

  const options = useMemo(
    () =>
      (query.data?.pages ?? []).flatMap((page) =>
        page.data.map((sensor) => ({
          value: sensor.id,
          label: sensor.device.code
            ? `${sensor.name} · ${sensor.device.code}`
            : sensor.name,
        })),
      ),
    [query.data],
  );

  return {
    options,
    searchValue: searchTerm,
    onSearchChange: setSearchTerm,
    isLoading: query.isLoading,
    isFetchingMore: query.isFetchingNextPage,
    hasMore: query.hasNextPage,
    onLoadMore: () => {
      void query.fetchNextPage();
    },
  };
}
